/**
 * Backoffice scolarité : Sujets d'examen reçus.
 *
 * - Onglet Sessions : créer/gérer les sessions de dépôt
 * - Onglet Dépôts : voir les sujets reçus d'une session, télécharger, verrouiller/débloquer
 * - Onglet Stats : qui a déposé, qui manque
 */
import React, { useState, useMemo } from 'react';
import {
  ShieldCheck, Library, Plus, Upload, Loader2, Lock, Unlock, Trash2,
  X, Calendar, FileText, AlertTriangle, BarChart3, CheckCircle2, Eye,
  Users, UserX, Mail, Download, Merge, Check,
} from 'lucide-react';
import {
  useSessionsDepotQuery, useCreerSessionDepotMutation, useUpdateSessionDepotMutation,
  useSupprimerSessionDepotMutation,
  useAdminDepotsSujetsQuery, useAdminUploaderDepotMutation, useAdminActionDepotMutation,
  useAdminSupprimerDepotMutation, useStatsDepotsSessionQuery,
  useLazyApercuFusionSessionsQuery, useFusionnerSessionsMutation,
  useAdminDemandesModifQuery, useAdminDeciderDemandeModifMutation,
  type SessionDepot,
} from '../../services/formateurApi';
import { useEnseignantsDisponiblesCompteQuery } from '../../services/coreApi';
import { useGetFormateurMatieresQuery } from '../../services/formateursApi';
import { useGetPolesQuery } from '../../services/polesOptionsApi';
import { useAppSelector } from '../../store/hooks';

/**
 * Télécharge un sujet via fetch + Bearer JWT — un simple <a href> perdrait
 * l'auth car le navigateur n'envoie pas les headers Authorization sur les
 * navigations classiques (le token est en localStorage, pas en cookie).
 */
async function telechargerSujetAuth(fichierUrl: string, nomFichier: string): Promise<void> {
  const token = localStorage.getItem('auth_token') || localStorage.getItem('access_token');
  const resp = await fetch(fichierUrl, {
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    credentials: 'include',
  });
  if (!resp.ok) {
    let detail = `HTTP ${resp.status}`;
    try {
      const j = await resp.json();
      detail = j.detail || j.error || detail;
    } catch { /* body may not be JSON (403 nginx block) */ }
    throw new Error(detail);
  }
  const blob = await resp.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = nomFichier || 'sujet';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(objectUrl), 30_000);
}

type Onglet = 'sessions' | 'depots' | 'stats' | 'demandes';

export default function DepotSujetsAdmin() {
  const [onglet, setOnglet] = useState<Onglet>('sessions');
  const [sessionSelectionnee, setSessionSelectionnee] = useState<SessionDepot | null>(null);
  const [showCreer, setShowCreer] = useState(false);
  const [showUploadManuel, setShowUploadManuel] = useState(false);
  const [showFusion, setShowFusion] = useState<SessionDepot | null>(null);

  const { data: sessions, refetch: refetchSessions } = useSessionsDepotQuery();
  const { data: depots, refetch: refetchDepots } = useAdminDepotsSujetsQuery(
    sessionSelectionnee ? { session_id: sessionSelectionnee.id } : { session_id: 0 },
    { skip: !sessionSelectionnee },
  );
  const { data: stats } = useStatsDepotsSessionQuery(
    sessionSelectionnee?.id || 0,
    { skip: !sessionSelectionnee || onglet !== 'stats' },
  );

  const [updateSession] = useUpdateSessionDepotMutation();
  const [supprimerSession] = useSupprimerSessionDepotMutation();
  const [actionDepot] = useAdminActionDepotMutation();
  const [supprimerDepot] = useAdminSupprimerDepotMutation();

  // Demandes de modification de dépôt (verrou 48h) — poll léger pour badge
  const { data: demandesEnAttenteQ } = useAdminDemandesModifQuery(
    { statut: 'en_attente' },
    { pollingInterval: 60000 },
  );
  const nbDemandesEnAttente = demandesEnAttenteQ?.nb_en_attente ?? 0;

  const handleChangerStatut = async (s: SessionDepot, statut: SessionDepot['statut']) => {
    const messages: Record<string, string> = {
      ouverte: 'Ré-ouvrir cette session ?',
      fermee: 'Fermer cette session (plus de dépôts possibles) ?',
      debloquee: 'Débloquer tous les sujets pour impression ? Les fichiers seront accessibles aux personnes autorisées.',
      archivee: 'Archiver cette session ?',
    };
    if (!window.confirm(messages[statut] || 'Changer le statut ?')) return;
    try {
      await updateSession({ id: s.id, statut }).unwrap();
      refetchSessions();
    } catch (err: any) {
      alert(err?.data?.detail || 'Erreur');
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-3 sm:px-6 py-4 sm:py-6">
      <header className="mb-4">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <ShieldCheck size={22} className="text-blue-600" /> Sujets d'examen (dépôt confidentiel)
        </h1>
        <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-1">
          Réception des sujets formateurs, verrouillage et déblocage pour impression.
        </p>
      </header>

      {/* Onglets */}
      <div className="border-b border-gray-200 dark:border-gray-700 mb-4 flex gap-1 overflow-x-auto">
        {(['sessions', 'depots', 'stats', 'demandes'] as const).map(o => (
          <button key={o} onClick={() => setOnglet(o)}
            className={`px-3 py-2 text-sm font-medium border-b-2 transition flex items-center gap-1.5 ${
              onglet === o
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
            }`}>
            {o === 'sessions' && <><Calendar size={14} /> Sessions</>}
            {o === 'depots' && <><FileText size={14} /> Sujets reçus
              {sessionSelectionnee && <span className="text-[10px] opacity-70">({depots?.total ?? 0})</span>}
            </>}
            {o === 'stats' && <><BarChart3 size={14} /> Statistiques</>}
            {o === 'demandes' && <><AlertTriangle size={14} /> Demandes modif
              {nbDemandesEnAttente > 0 && (
                <span className="ml-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-red-500 text-white">
                  {nbDemandesEnAttente}
                </span>
              )}
            </>}
          </button>
        ))}
        {sessionSelectionnee && onglet !== 'sessions' && (
          <div className="ml-auto self-end pb-2 text-[11px] text-gray-500 truncate">
            Session : <strong className="text-amber-600 dark:text-amber-400">{sessionSelectionnee.libelle}</strong>
          </div>
        )}
      </div>

      {/* Onglet Sessions */}
      {onglet === 'sessions' && (
        <>
          <div className="flex justify-end mb-3">
            <button onClick={() => setShowCreer(true)}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded inline-flex items-center gap-1.5">
              <Plus size={14} /> Nouvelle session
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {(sessions?.results || []).map(s => (
              <div key={s.id}
                className={`bg-white dark:bg-gray-800 border rounded-xl p-4 ${
                  s.statut === 'ouverte' ? 'border-emerald-200 dark:border-emerald-700/60'
                    : s.statut === 'debloquee' ? 'border-red-200 dark:border-red-700/60'
                    : 'border-gray-200 dark:border-gray-700'
                }`}>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-bold text-gray-900 dark:text-white truncate">{s.libelle}</h3>
                    <div className="text-[11px] text-gray-500 mt-0.5">
                      {s.annee_academique} • {s.semestre} • {s.type_session === 'rattrapage' ? 'Rattrapage' : 'Normale'}
                    </div>
                  </div>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${
                    s.statut === 'ouverte' ? 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300'
                    : s.statut === 'fermee' ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300'
                    : s.statut === 'debloquee' ? 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                  }`}>
                    {s.statut === 'ouverte' && 'OUVERTE'}
                    {s.statut === 'fermee' && 'FERMÉE'}
                    {s.statut === 'debloquee' && 'DÉBLOQUÉE'}
                    {s.statut === 'archivee' && 'ARCHIVÉE'}
                  </span>
                </div>
                <div className="text-[11px] text-gray-500 mb-2 flex items-center gap-2 flex-wrap">
                  {s.nb_depots !== undefined && (
                    <span>{s.nb_depots} dépôt{s.nb_depots > 1 ? 's' : ''}</span>
                  )}
                  {s.poles && s.poles.length > 0 ? (
                    <span className="inline-flex items-center gap-1 flex-wrap">
                      {s.poles.map(p => (
                        <span key={p}
                          className="text-[10px] px-1.5 py-0.5 bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 rounded font-semibold">
                          {p}
                        </span>
                      ))}
                    </span>
                  ) : (
                    <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded italic">
                      Tous pôles
                    </span>
                  )}
                </div>
                {/* Traçabilité : créateur + date de création */}
                <div className="text-[10px] text-gray-400 dark:text-gray-500 mb-3 space-y-0.5 border-t border-gray-100 dark:border-gray-700/50 pt-2">
                  <div className="inline-flex items-center gap-1">
                    <Users size={10} />
                    Créée par <strong className="text-gray-600 dark:text-gray-300">{s.cree_par_nom || '—'}</strong>
                  </div>
                  <div className="inline-flex items-center gap-1">
                    <Calendar size={10} />
                    {s.date_creation
                      ? new Date(s.date_creation).toLocaleString('fr-FR', {
                          day: '2-digit', month: '2-digit', year: 'numeric',
                          hour: '2-digit', minute: '2-digit',
                        })
                      : '—'}
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5 text-[11px]">
                  <button onClick={() => { setSessionSelectionnee(s); setOnglet('depots'); }}
                    className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded hover:bg-blue-200 dark:hover:bg-blue-900/50 inline-flex items-center gap-1">
                    <Eye size={11} /> Voir
                  </button>
                  {s.statut === 'ouverte' && (
                    <button onClick={() => handleChangerStatut(s, 'fermee')}
                      className="px-2 py-1 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded hover:bg-amber-200 dark:hover:bg-amber-900/50 inline-flex items-center gap-1">
                      <Lock size={11} /> Fermer
                    </button>
                  )}
                  {s.statut === 'fermee' && (
                    <>
                      <button onClick={() => handleChangerStatut(s, 'ouverte')}
                        className="px-2 py-1 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 rounded inline-flex items-center gap-1">
                        Ré-ouvrir
                      </button>
                      <button onClick={() => handleChangerStatut(s, 'debloquee')}
                        className="px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded hover:bg-red-200 dark:hover:bg-red-900/50 inline-flex items-center gap-1 font-semibold">
                        <Unlock size={11} /> Débloquer
                      </button>
                    </>
                  )}
                  {s.statut === 'debloquee' && (
                    <button onClick={() => handleChangerStatut(s, 'fermee')}
                      className="px-2 py-1 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded inline-flex items-center gap-1">
                      <Lock size={11} /> Re-verrouiller
                    </button>
                  )}
                  {(s.statut !== 'debloquee' && (s.nb_depots ?? 0) === 0) && (
                    <button onClick={async () => {
                      if (!window.confirm(`Supprimer la session « ${s.libelle} » ?`)) return;
                      try { await supprimerSession(s.id).unwrap(); refetchSessions(); } catch {}
                    }}
                      className="px-2 py-1 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded inline-flex items-center gap-1">
                      <Trash2 size={11} />
                    </button>
                  )}
                  {/* Fusionner : dispo dès qu'une autre session existe (doublon
                      de saisie). L'aperçu backend confirme les impacts. */}
                  {(sessions?.results || []).length > 1 && (
                    <button onClick={() => setShowFusion(s)}
                      className="px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded hover:bg-purple-200 dark:hover:bg-purple-900/50 inline-flex items-center gap-1"
                      title="Fusionner cette session avec une autre">
                      <Merge size={11} /> Fusionner
                    </button>
                  )}
                </div>
              </div>
            ))}
            {(sessions?.results || []).length === 0 && (
              <div className="col-span-full text-center py-8 text-gray-500 text-sm">
                Aucune session pour le moment. Créez-en une pour commencer.
              </div>
            )}
          </div>
        </>
      )}

      {/* Onglet Dépôts */}
      {onglet === 'depots' && sessionSelectionnee && (
        <>
          <div className="flex justify-end mb-3 gap-2">
            <button onClick={() => setShowUploadManuel(true)}
              disabled={sessionSelectionnee.statut === 'archivee'}
              className="px-3 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-semibold rounded inline-flex items-center gap-1.5">
              <Upload size={14} /> Upload manuel (clé USB)
            </button>
          </div>

          {/* Bloc : brassages associés à cette session (pour effectifs par site) */}
          <BrassagesAssociesBloc
            session={sessionSelectionnee}
            brassagesUtilises={(depots as any)?.brassages_utilises || []}
            brassagesDisponibles={(depots as any)?.brassages_disponibles || []}
            brassageIdsPersistes={(depots as any)?.brassage_ids_persistes || []}
            onUpdated={() => { refetchDepots(); refetchSessions(); }}
          />

          {/* Affichage regroupé par formateur — une ligne = une matière (fusion
              SO+SR côte à côte, et si le sujet est en tronc commun sur plusieurs
              classes/options, elles sont listées dans la colonne Classes). */}
          {(() => {
            const groupesBackend = (depots as any)?.groupes as Array<any> | undefined;
            const rows = depots?.results || [];
            if (rows.length === 0) {
              return (
                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-8 text-center text-gray-500 text-sm">
                  Aucun dépôt pour cette session.
                </div>
              );
            }
            // Utiliser les groupes du backend si disponibles (regroupement par
            // formateur + matière + niveau + type_sujet). On fusionne ensuite
            // par (formateur + matière + niveau) pour avoir SO et SR côte à côte.
            type Grp = {
              formateur_user_id: number | null;
              formateur_nom: string;
              matiere_libelle: string;
              matiere_code: string;
              niveau: string;
              type_sujet: string;
              classes: string[];
              options: string[];
              depots: any[];
              date_derniere: string | null;
              statut_agrege: string;
            };
            let groupes: Grp[];
            if (groupesBackend && groupesBackend.length > 0) {
              groupes = groupesBackend as Grp[];
            } else {
              // Fallback : re-grouper côté client à partir des results
              const m = new Map<string, Grp>();
              for (const d of rows) {
                const key = `${d.formateur_user_id ?? d.formateur_nom}|${(d.matiere_libelle || '').toLowerCase()}|${d.niveau}|${d.type_sujet || 'ordinaire'}`;
                if (!m.has(key)) {
                  m.set(key, {
                    formateur_user_id: d.formateur_user_id ?? null,
                    formateur_nom: d.formateur_nom || '—',
                    matiere_libelle: d.matiere_libelle || '',
                    matiere_code: d.matiere_code || '',
                    niveau: d.niveau || '',
                    type_sujet: d.type_sujet || 'ordinaire',
                    classes: [],
                    options: [],
                    depots: [],
                    date_derniere: null,
                    statut_agrege: '',
                  });
                }
                const g = m.get(key)!;
                g.depots.push(d);
                if (d.classe && !g.classes.includes(d.classe)) g.classes.push(d.classe);
                if (d.option && !g.options.includes(d.option)) g.options.push(d.option);
              }
              groupes = Array.from(m.values());
            }
            // Fusion 2ᵉ niveau : pour chaque (formateur, matière, niveau),
            // combiner SO et SR sur une seule ligne.
            type Paire = {
              key: string;
              formateur_user_id: number | null;
              formateur_nom: string;
              matiere_libelle: string;
              matiere_code: string;
              niveau: string;
              classes: string[];
              options: string[];
              so?: Grp;
              sr?: Grp;
            };
            const paires = new Map<string, Paire>();
            for (const g of groupes) {
              const key = `${g.formateur_user_id ?? g.formateur_nom}|${g.matiere_libelle.toLowerCase()}|${g.niveau}`;
              if (!paires.has(key)) {
                paires.set(key, {
                  key,
                  formateur_user_id: g.formateur_user_id,
                  formateur_nom: g.formateur_nom,
                  matiere_libelle: g.matiere_libelle,
                  matiere_code: g.matiere_code,
                  niveau: g.niveau,
                  classes: [],
                  options: [],
                });
              }
              const p = paires.get(key)!;
              if (g.type_sujet === 'rattrapage') p.sr = g;
              else p.so = g;
              for (const c of g.classes) if (!p.classes.includes(c)) p.classes.push(c);
              for (const o of g.options) if (!p.options.includes(o)) p.options.push(o);
            }
            // Regrouper les paires par formateur pour l'en-tête
            const groupsMap = new Map<string, { key: string; formateur_nom: string; paires: Paire[]; nb_so: number; nb_sr: number }>();
            for (const p of Array.from(paires.values())) {
              const key = String(p.formateur_user_id ?? `nom:${p.formateur_nom}`);
              if (!groupsMap.has(key)) {
                groupsMap.set(key, { key, formateur_nom: p.formateur_nom, paires: [], nb_so: 0, nb_sr: 0 });
              }
              const gg = groupsMap.get(key)!;
              gg.paires.push(p);
              if (p.so) gg.nb_so++;
              if (p.sr) gg.nb_sr++;
            }
            const groups = Array.from(groupsMap.values()).sort((a, b) =>
              a.formateur_nom.localeCompare(b.formateur_nom, 'fr'),
            );
            // Helper : rendu d'une cellule de dépôt (SO ou SR)
            const renderDepotCell = (g?: any) => {
              if (!g) return <span className="text-gray-300 italic text-[10px]">—</span>;
              const dep = g.depots[0]; // dépôt canonique pour affichage
              const statut = g.statut_agrege || dep?.statut || '';
              return (
                <div className="space-y-1">
                  <div className="flex items-center gap-1 flex-wrap">
                    {dep?.fichier_url ? (
                      <button
                        type="button"
                        onClick={async () => {
                          try { await telechargerSujetAuth(dep.fichier_url, dep.nom_original); }
                          catch (err: any) { alert(`Téléchargement refusé : ${err.message || err}`); }
                        }}
                        className="text-blue-500 hover:underline inline-flex items-center gap-1 cursor-pointer text-xs"
                        title="Télécharger le sujet"
                      >
                        <Download size={11} />
                        {(dep.nom_original || '').length > 18 ? (dep.nom_original || '').slice(0, 16) + '…' : dep.nom_original}
                      </button>
                    ) : (
                      <span className="text-gray-400 italic inline-flex items-center gap-1 text-xs" title="Confidentiel">
                        <Lock size={11} /> Confidentiel
                      </span>
                    )}
                    <span className={`text-[9px] font-bold px-1 py-0.5 rounded ${
                      statut === 'debloque' ? 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300'
                      : statut === 'verrouille' ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300'
                      : 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300'
                    }`}>
                      {statut === 'depose' && 'ATTENTE'}
                      {statut === 'verrouille' && 'VERROUILLÉ'}
                      {statut === 'debloque' && 'IMPRIMABLE'}
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    {statut === 'depose' && (
                      <button
                        onClick={() => g.depots.forEach((d: any) => actionDepot({ depotId: d.id, action: 'verrouiller' }))}
                        className="p-1 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded"
                        title={`Verrouiller (${g.depots.length} dépôt${g.depots.length > 1 ? 's' : ''})`}
                      >
                        <Lock size={11} />
                      </button>
                    )}
                    {statut === 'verrouille' && (
                      <button
                        onClick={() => {
                          if (window.confirm(`Débloquer ${g.depots.length} sujet${g.depots.length > 1 ? 's' : ''} pour impression ?`))
                            g.depots.forEach((d: any) => actionDepot({ depotId: d.id, action: 'debloquer' }));
                        }}
                        className="p-1 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded"
                        title="Débloquer pour impression"
                      >
                        <Unlock size={11} />
                      </button>
                    )}
                    {statut === 'debloque' && (
                      <button
                        onClick={() => g.depots.forEach((d: any) => actionDepot({ depotId: d.id, action: 'reverrouiller' }))}
                        className="p-1 text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/30 rounded"
                        title="Re-verrouiller"
                      >
                        <Lock size={11} />
                      </button>
                    )}
                    <button
                      onClick={async () => {
                        if (!window.confirm(`Supprimer ${g.depots.length} dépôt${g.depots.length > 1 ? 's' : ''} pour « ${g.matiere_libelle} » ?`)) return;
                        for (const d of g.depots) {
                          try { await supprimerDepot(d.id).unwrap(); } catch { /* noop */ }
                        }
                        refetchDepots();
                      }}
                      className="p-1 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded"
                      title="Supprimer"
                    >
                      <Trash2 size={11} />
                    </button>
                  </div>
                </div>
              );
            };

            return (
              <div className="space-y-3">
                {groups.map((g) => {
                  return (
                  <div key={g.key} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                    {/* En-tête formateur avec compteurs SO/SR */}
                    <div className="px-4 py-2 bg-gradient-to-r from-blue-50 to-transparent dark:from-blue-900/20 dark:to-transparent border-b border-gray-200 dark:border-gray-700 flex items-center gap-3">
                      <Users size={16} className="text-blue-600 dark:text-blue-400 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-gray-900 dark:text-white truncate">{g.formateur_nom}</div>
                        <div className="text-[10px] text-gray-500 dark:text-gray-400 flex items-center gap-2 mt-0.5">
                          <span className="inline-flex items-center gap-1">
                            <span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-500" />
                            {g.nb_so} ordinaire{g.nb_so > 1 ? 's' : ''}
                          </span>
                          <span className="text-gray-300">·</span>
                          <span className="inline-flex items-center gap-1">
                            <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-500" />
                            {g.nb_sr} rattrapage
                          </span>
                        </div>
                      </div>
                    </div>
                    {/* Tableau : 1 ligne par matière, SO et SR côte à côte */}
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 dark:bg-gray-900/40 text-[10px] uppercase text-gray-500">
                        <tr>
                          <th className="px-3 py-1.5 text-left">Matière</th>
                          <th className="px-3 py-1.5 text-left">Niveau</th>
                          <th className="px-3 py-1.5 text-left">Classes</th>
                          <th className="px-3 py-1.5 text-left">Copies à imprimer (par site)</th>
                          <th className="px-3 py-1.5 text-left w-64">Sujet SO</th>
                          <th className="px-3 py-1.5 text-left w-64">Sujet SR</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
                        {g.paires.map((p) => {
                          // Effectifs par site : union SO/SR (identique en pratique)
                          const sitesFromGroup = (p.so as any)?.sites_effectifs
                            || (p.sr as any)?.sites_effectifs
                            || [];
                          const totalCopies = (p.so as any)?.effectif_total
                            ?? (p.sr as any)?.effectif_total
                            ?? 0;
                          const brassageOk = (p.so as any)?.brassage_source
                            ?? (p.sr as any)?.brassage_source
                            ?? false;
                          return (
                            <tr key={p.key} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                              <td className="px-3 py-2 text-sm text-gray-700 dark:text-gray-300">
                                <div className="font-medium">{p.matiere_libelle}</div>
                                {p.matiere_code && <div className="text-[10px] text-gray-500">{p.matiere_code}</div>}
                              </td>
                              <td className="px-3 py-2 text-xs">
                                <span className="px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded font-mono">
                                  {p.niveau}
                                </span>
                              </td>
                              <td className="px-3 py-2">
                                <div className="flex flex-wrap gap-1">
                                  {p.classes.length > 0 ? p.classes.map((c) => (
                                    <span key={c} className="text-[10px] px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded font-mono">
                                      {c}
                                    </span>
                                  )) : (p.options.length > 0 ? p.options.map((o) => (
                                    <span key={o} className="text-[10px] px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded">
                                      {o}
                                    </span>
                                  )) : <span className="text-[10px] text-gray-400 italic">—</span>)}
                                </div>
                              </td>
                              <td className="px-3 py-2 text-xs">
                                {brassageOk && sitesFromGroup.length > 0 ? (
                                  <div className="flex flex-col gap-0.5">
                                    <div className="flex flex-wrap gap-1">
                                      {sitesFromGroup.map((s: any) => (
                                        <span
                                          key={s.site_nom}
                                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border bg-emerald-50 dark:bg-emerald-900/20 border-emerald-300 dark:border-emerald-700 text-emerald-800 dark:text-emerald-300 font-medium"
                                          title={`${s.effectif} étudiant(s) placé(s) sur ${s.site_nom}`}
                                        >
                                          {s.site_nom}
                                          <strong className="ml-0.5 text-emerald-700 dark:text-emerald-200">{s.effectif}</strong>
                                        </span>
                                      ))}
                                    </div>
                                    <div className="text-[10px] text-gray-500 dark:text-gray-400">
                                      Total : <strong className="text-gray-700 dark:text-gray-200">{totalCopies}</strong> copies
                                    </div>
                                  </div>
                                ) : brassageOk ? (
                                  <span className="text-[11px] text-gray-400 italic">Aucun étudiant placé</span>
                                ) : (
                                  <span className="text-[11px] text-amber-600 dark:text-amber-400" title="Lancez le brassage de la session d'examens correspondante">
                                    ⚠ Pas de brassage
                                  </span>
                                )}
                              </td>
                              <td className="px-3 py-2 border-l border-gray-100 dark:border-gray-700/50 bg-blue-50/30 dark:bg-blue-950/10">
                                {renderDepotCell(p.so)}
                              </td>
                              <td className="px-3 py-2 border-l border-gray-100 dark:border-gray-700/50 bg-amber-50/30 dark:bg-amber-950/10">
                                {renderDepotCell(p.sr)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  );
                })}
              </div>
            );
          })()}
        </>
      )}

      {/* Onglet Stats */}
      {onglet === 'stats' && sessionSelectionnee && stats && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
            <Kpi label="Total dépôts" v={stats.kpi.total_depots} color="blue" />
            <Kpi label="En attente" v={stats.kpi.depose} color="amber" />
            <Kpi label="Verrouillés" v={stats.kpi.verrouille} color="purple" />
            <Kpi label="Débloqués" v={stats.kpi.debloque} color="red" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* Déposants */}
            <div className="bg-white dark:bg-gray-800 border border-emerald-200 dark:border-emerald-800 rounded-xl p-3">
              <h3 className="text-sm font-bold text-emerald-700 dark:text-emerald-300 mb-2 inline-flex items-center gap-1.5">
                <CheckCircle2 size={14} /> Ont déposé ({stats.deposants.length})
              </h3>
              <div className="space-y-1 max-h-96 overflow-y-auto">
                {stats.deposants.map(d => (
                  <div key={d.formateur_user_id} className="px-2 py-1 bg-emerald-50 dark:bg-emerald-900/10 rounded text-xs flex items-center justify-between">
                    <span className="text-gray-900 dark:text-gray-100 truncate">{d.formateur_nom}</span>
                    <span className="font-bold text-emerald-600 dark:text-emerald-400">
                      {d.nb_depots} sujet{d.nb_depots > 1 ? 's' : ''}
                    </span>
                  </div>
                ))}
                {stats.deposants.length === 0 && (
                  <div className="text-center py-4 text-xs text-gray-500">Aucun dépôt encore.</div>
                )}
              </div>
            </div>

            {/* Manquants */}
            <div className="bg-white dark:bg-gray-800 border border-red-200 dark:border-red-800 rounded-xl p-3">
              <h3 className="text-sm font-bold text-red-700 dark:text-red-300 mb-2 inline-flex items-center gap-1.5">
                <UserX size={14} /> N'ont pas (totalement) déposé ({stats.manquants.length})
              </h3>
              <div className="space-y-1.5 max-h-96 overflow-y-auto">
                {stats.manquants.map(m => (
                  <div key={m.formateur_user_id} className="px-2 py-1.5 bg-red-50 dark:bg-red-900/10 rounded text-xs">
                    <div className="flex items-center justify-between mb-0.5">
                      <span className="font-medium text-gray-900 dark:text-gray-100">{m.formateur_nom}</span>
                      <span className="text-[10px] text-red-600 dark:text-red-400 font-bold">
                        {m.nb_deposes}/{m.nb_attendus}
                      </span>
                    </div>
                    {m.email && (
                      <a href={`mailto:${m.email}`}
                        className="text-[10px] text-blue-500 hover:underline inline-flex items-center gap-0.5">
                        <Mail size={9} /> {m.email}
                      </a>
                    )}
                    {m.matieres_non_deposees.length > 0 && (
                      <div className="text-[10px] text-gray-600 dark:text-gray-400 mt-0.5">
                        Manque : {m.matieres_non_deposees.map(x => x.matiere_libelle).join(', ')}
                      </div>
                    )}
                  </div>
                ))}
                {stats.manquants.length === 0 && (
                  <div className="text-center py-4 text-xs text-emerald-600 dark:text-emerald-400">
                    🎉 Tous les formateurs ont déposé !
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Onglet Demandes de modification */}
      {onglet === 'demandes' && (
        <DemandesModifPanel sessions={sessions?.results || []} />
      )}

      {showCreer && (
        <ModalCreerSession onClose={() => setShowCreer(false)}
          onDone={() => { setShowCreer(false); refetchSessions(); }} />
      )}
      {showUploadManuel && sessionSelectionnee && (
        <ModalUploadManuel session={sessionSelectionnee}
          onClose={() => setShowUploadManuel(false)}
          onDone={() => { setShowUploadManuel(false); refetchDepots(); }} />
      )}
      {showFusion && (
        <ModalFusionSessions
          source={showFusion}
          candidates={(sessions?.results || []).filter(s => s.id !== showFusion.id)}
          onClose={() => setShowFusion(null)}
          onDone={() => { setShowFusion(null); refetchSessions(); refetchDepots(); }}
        />
      )}
    </div>
  );
}

// ─── Modal : fusion de sessions doublons (2026-07-22) ─────────────────────
function ModalFusionSessions({
  source, candidates, onClose, onDone,
}: {
  source: SessionDepot;
  candidates: SessionDepot[];
  onClose: () => void;
  onDone: () => void;
}) {
  const [cibleId, setCibleId] = useState<number | null>(candidates[0]?.id || null);
  const [apercu, { data: preview, isFetching }] = useLazyApercuFusionSessionsQuery();
  const [fusionner, { isLoading: pending }] = useFusionnerSessionsMutation();
  const [msg, setMsg] = useState<string | null>(null);

  React.useEffect(() => {
    if (cibleId) apercu({ source_id: source.id, cible_id: cibleId });
  }, [source.id, cibleId, apercu]);

  const handleFusion = async () => {
    if (!cibleId) return;
    setMsg(null);
    try {
      const res = await fusionner({ source_id: source.id, cible_id: cibleId }).unwrap();
      setMsg(res.message);
      setTimeout(() => onDone(), 1200);
    } catch (err: any) {
      setMsg(err?.data?.detail || 'Erreur lors de la fusion.');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white dark:bg-gray-800 rounded-xl w-full max-w-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <header className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h3 className="text-base font-bold text-gray-900 dark:text-white inline-flex items-center gap-2">
            <Merge size={18} className="text-purple-600" /> Fusionner deux sessions
          </h3>
          <button onClick={onClose}><X size={18} /></button>
        </header>
        <div className="p-5 space-y-4">
          <div className="text-xs text-gray-500">
            Fusionner deux sessions créées par erreur pour la même période. Les dépôts de la
            source sont transférés vers la cible ; les doublons (même formateur + matière + type)
            sont dédupliqués en gardant le plus récent. La source est supprimée.
          </div>

          <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 bg-gray-50 dark:bg-gray-900/40">
            <div className="text-[10px] uppercase text-gray-500 font-semibold mb-1">Source (à supprimer)</div>
            <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">{source.libelle}</div>
            <div className="text-xs text-gray-500 mt-0.5">
              {source.annee_academique} • {source.semestre} • {(source as any).nb_depots ?? 0} dépôts
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
              Session cible (à conserver)
            </label>
            <select
              value={cibleId ?? ''}
              onChange={(e) => setCibleId(e.target.value ? Number(e.target.value) : null)}
              className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded"
            >
              <option value="">— Choisir —</option>
              {candidates.map(c => (
                <option key={c.id} value={c.id}>
                  {c.libelle} ({c.annee_academique} · {c.semestre}) — {(c as any).nb_depots ?? 0} dépôts
                </option>
              ))}
            </select>
          </div>

          {isFetching && <div className="text-xs text-gray-500"><Loader2 size={12} className="inline animate-spin mr-1" /> Analyse en cours…</div>}

          {preview && !isFetching && (
            <div className="rounded-lg border border-purple-200 dark:border-purple-800 bg-purple-50/50 dark:bg-purple-900/20 p-3">
              <div className="text-xs font-semibold text-purple-800 dark:text-purple-300 mb-2">Aperçu de la fusion</div>
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div>
                  <div className="font-bold text-2xl text-emerald-700 dark:text-emerald-400">{preview.nb_deplacables}</div>
                  <div className="text-gray-500">dépôt(s) déplaçable(s)</div>
                </div>
                <div>
                  <div className="font-bold text-2xl text-amber-700 dark:text-amber-400">{preview.nb_doublons}</div>
                  <div className="text-gray-500">doublon(s) à dédupliquer</div>
                </div>
              </div>
              {preview.nb_doublons > 0 && (
                <div className="mt-3 text-[10px] text-gray-600 dark:text-gray-400">
                  <strong>Règle de déduplication :</strong> le dépôt le plus récent est conservé.
                </div>
              )}
            </div>
          )}

          {msg && (
            <div className={`rounded p-2 text-xs ${msg.includes('OK') || msg.includes('déplacés')
              ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-800 dark:text-emerald-300'
              : 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-300'}`}>
              {msg}
            </div>
          )}
        </div>
        <footer className="px-5 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
          <button onClick={onClose} disabled={pending}
            className="px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 rounded">Annuler</button>
          <button onClick={handleFusion} disabled={!cibleId || pending}
            className="px-4 py-1.5 text-sm bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white font-semibold rounded inline-flex items-center gap-1.5">
            {pending ? <Loader2 size={12} className="animate-spin" /> : <Merge size={12} />}
            Confirmer la fusion
          </button>
        </footer>
      </div>
    </div>
  );
}

// ─── Panel : demandes de modification à traiter (2026-07-22) ──────────────
function DemandesModifPanel({ sessions: _sessions }: { sessions: SessionDepot[] }) {
  const [statut, setStatut] = useState<'en_attente' | 'acceptee' | 'refusee' | 'expiree' | 'utilisee'>('en_attente');
  const { data, refetch, isFetching } = useAdminDemandesModifQuery({ statut });
  const [decider, { isLoading: deciding }] = useAdminDeciderDemandeModifMutation();
  const [modalDecision, setModalDecision] = useState<null | { id: number; decision: 'accepter' | 'refuser' }>(null);
  const [reponse, setReponse] = useState('');
  const [fenetreH, setFenetreH] = useState(24);

  const handleDecider = async () => {
    if (!modalDecision) return;
    try {
      await decider({
        demande_id: modalDecision.id,
        decision: modalDecision.decision,
        reponse: reponse || undefined,
        fenetre_heures: modalDecision.decision === 'accepter' ? fenetreH : undefined,
      }).unwrap();
      setModalDecision(null); setReponse(''); setFenetreH(24);
      refetch();
    } catch (err: any) {
      alert(err?.data?.detail || 'Erreur');
    }
  };

  const results = data?.results || [];

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <div className="text-xs text-gray-500 mr-2">Filtrer :</div>
        {(['en_attente', 'acceptee', 'refusee', 'expiree', 'utilisee'] as const).map(s => (
          <button key={s} onClick={() => setStatut(s)}
            className={`px-2 py-1 text-xs font-medium rounded ${
              statut === s
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
            }`}>
            {s.replace('_', ' ')}
          </button>
        ))}
        <div className="ml-auto text-xs text-gray-500">
          {isFetching ? <Loader2 size={10} className="inline animate-spin" /> : `${results.length} demande(s)`}
        </div>
      </div>

      {results.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-8 text-center text-gray-500 text-sm">
          Aucune demande.
        </div>
      ) : (
        <div className="space-y-2">
          {results.map(d => (
            <div key={d.id} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                    {d.matiere_libelle} <span className="text-[10px] text-gray-500 font-normal">({d.matiere_code || '—'})</span>
                    <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 font-mono">
                      {d.niveau}
                    </span>
                    <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-mono">
                      {d.classe || d.option}
                    </span>
                    <span className={`ml-1 text-[10px] px-1.5 py-0.5 rounded font-semibold ${
                      d.type_sujet === 'rattrapage'
                        ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-700'
                        : 'bg-blue-100 dark:bg-blue-900/40 text-blue-700'
                    }`}>
                      {d.type_sujet === 'rattrapage' ? 'SR' : 'SO'}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    Formateur : <strong className="text-gray-700 dark:text-gray-300">{d.formateur_nom}</strong> •
                    demandée le {new Date(d.date_demande).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                  </div>
                  <div className="mt-2 p-2 bg-gray-50 dark:bg-gray-900/40 rounded text-xs text-gray-700 dark:text-gray-300">
                    <span className="font-semibold">Motif :</span> {d.motif}
                  </div>
                  {d.reponse_scolarite && (
                    <div className="mt-1 text-[10px] text-gray-500">Réponse : {d.reponse_scolarite}</div>
                  )}
                  {d.autorisation_expire_le && (
                    <div className="mt-1 text-[10px] text-emerald-600 dark:text-emerald-400">
                      Fenêtre ouverte jusqu'au {new Date(d.autorisation_expire_le).toLocaleString('fr-FR')}
                    </div>
                  )}
                </div>
                {d.statut === 'en_attente' && (
                  <div className="flex flex-col gap-1 shrink-0">
                    <button onClick={() => { setModalDecision({ id: d.id, decision: 'accepter' }); setFenetreH(24); setReponse(''); }}
                      className="px-2 py-1 text-xs bg-emerald-600 hover:bg-emerald-700 text-white rounded inline-flex items-center gap-1">
                      <Check size={11} /> Accepter
                    </button>
                    <button onClick={() => { setModalDecision({ id: d.id, decision: 'refuser' }); setReponse(''); }}
                      className="px-2 py-1 text-xs bg-red-600 hover:bg-red-700 text-white rounded inline-flex items-center gap-1">
                      <X size={11} /> Refuser
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal de décision */}
      {modalDecision && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setModalDecision(null)}>
          <div className="bg-white dark:bg-gray-800 rounded-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <header className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h3 className="text-sm font-bold text-gray-900 dark:text-white">
                {modalDecision.decision === 'accepter' ? 'Accepter la demande' : 'Refuser la demande'}
              </h3>
              <button onClick={() => setModalDecision(null)}><X size={16} /></button>
            </header>
            <div className="p-4 space-y-3">
              {modalDecision.decision === 'accepter' && (
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                    Fenêtre d'autorisation (heures)
                  </label>
                  <input type="number" min={1} max={168} value={fenetreH}
                    onChange={(e) => setFenetreH(Math.max(1, Math.min(168, Number(e.target.value) || 24)))}
                    className="w-24 px-3 py-2 text-sm bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded" />
                  <div className="text-[10px] text-gray-500 mt-1">Le formateur pourra modifier son dépôt pendant cette durée.</div>
                </div>
              )}
              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  Message au formateur (optionnel)
                </label>
                <textarea value={reponse} onChange={(e) => setReponse(e.target.value)} rows={3} maxLength={2000}
                  className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded resize-none" />
              </div>
            </div>
            <footer className="px-5 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
              <button onClick={() => setModalDecision(null)} disabled={deciding}
                className="px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 rounded">Annuler</button>
              <button onClick={handleDecider} disabled={deciding}
                className={`px-3 py-1.5 text-sm text-white rounded inline-flex items-center gap-1.5 ${
                  modalDecision.decision === 'accepter'
                    ? 'bg-emerald-600 hover:bg-emerald-700'
                    : 'bg-red-600 hover:bg-red-700'
                } disabled:opacity-60`}>
                {deciding ? <Loader2 size={12} className="animate-spin" /> : (modalDecision.decision === 'accepter' ? <Check size={12} /> : <X size={12} />)}
                Confirmer
              </button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}

function Kpi({ label, v, color }: { label: string; v: number; color: string }) {
  const map: Record<string, string> = {
    blue: 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800',
    amber: 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800',
    purple: 'bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-800',
    red: 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border-red-200 dark:border-red-800',
  };
  return (
    <div className={`px-3 py-2 rounded-lg border text-center ${map[color]}`}>
      <div className="text-2xl font-bold">{v}</div>
      <div className="text-[10px] uppercase font-semibold">{label}</div>
    </div>
  );
}

function ModalCreerSession({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const today = new Date().toISOString().slice(0, 10);
  const [libelle, setLibelle] = useState('');
  const [annee, setAnnee] = useState('2025-2026');
  const [semestre, setSemestre] = useState<'S1' | 'S2'>('S1');
  const [type, setType] = useState<'normale' | 'rattrapage'>('normale');
  const [dateDebut, setDateDebut] = useState(today);
  const [dateFin, setDateFin] = useState('');
  const [dateExamens, setDateExamens] = useState('');
  const [note, setNote] = useState('');
  const [nbSujetsRequis, setNbSujetsRequis] = useState<number>(2);
  const [erreur, setErreur] = useState('');

  // Sélection des pôles couverts par cette session (isolation par pôle).
  // - super_admin/admin : voit et coche tous les pôles (par défaut aucun = session globale)
  // - agent : ne voit que ses pôles autorisés, pré-cochés
  const user = useAppSelector(state => state.auth.user);
  const { data: tousLesPoles } = useGetPolesQuery({ actif: true });
  const estAdmin = user?.role === 'super_admin' || user?.role === 'admin' || user?.role === 'directeur';
  const polesUtilisateur = user?.poles_autorises || [];
  const polesDisponibles = React.useMemo(() => {
    const liste = (tousLesPoles || []).map(p => p.nom).filter(Boolean);
    if (estAdmin) return liste;
    // Agent : restreint aux pôles où il a un accès permanent
    return liste.filter(n => polesUtilisateur.includes(n));
  }, [tousLesPoles, estAdmin, polesUtilisateur]);
  const [polesSelectionnes, setPolesSelectionnes] = useState<string[]>([]);

  // Pré-cocher automatiquement les pôles de l'agent une fois la liste chargée
  React.useEffect(() => {
    if (polesSelectionnes.length === 0 && polesDisponibles.length > 0 && !estAdmin) {
      setPolesSelectionnes(polesDisponibles);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [polesDisponibles.length]);

  // Quand le type change, ajuste le nb requis : 2 pour normale, 1 pour rattrapage
  useMemo(() => {
    setNbSujetsRequis(type === 'rattrapage' ? 1 : 2);
  }, [type]);
  const [creer, { isLoading }] = useCreerSessionDepotMutation();

  // Auto-libellé
  useMemo(() => {
    if (!libelle) {
      setLibelle(`Examens ${semestre} ${annee} - ${type === 'rattrapage' ? 'Rattrapage' : 'Normale'}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [semestre, annee, type]);

  const handle = async (e: React.FormEvent) => {
    e.preventDefault();
    setErreur('');
    if (!libelle || !annee || !semestre) {
      setErreur('Libellé, année, semestre requis');
      return;
    }
    try {
      await creer({
        libelle, annee_academique: annee, semestre, type_session: type,
        date_debut_depot: dateDebut || undefined,
        date_fin_depot: dateFin || undefined,
        date_examens: dateExamens || undefined,
        note_admin: note,
        nombre_sujets_requis_par_matiere: nbSujetsRequis,
        poles: polesSelectionnes,
      }).unwrap();
      onDone();
    } catch (err: any) {
      setErreur(err?.data?.detail || 'Erreur');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}>
      <form onSubmit={handle} onClick={(e) => e.stopPropagation()}
        className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md border border-gray-200 dark:border-gray-700 max-h-[90vh] overflow-y-auto">
        <header className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white">Nouvelle session</h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={18} />
          </button>
        </header>
        <div className="p-5 space-y-3">
          <div>
            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Libellé</label>
            <input value={libelle} onChange={(e) => setLibelle(e.target.value)} maxLength={200} required
              className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-gray-100" />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Année</label>
              <input value={annee} onChange={(e) => setAnnee(e.target.value)} placeholder="2025-2026" required
                className="w-full px-2 py-2 text-sm bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-gray-100" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Semestre</label>
              <select value={semestre} onChange={(e) => setSemestre(e.target.value as any)}
                className="w-full px-2 py-2 text-sm bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-gray-100">
                <option value="S1">S1</option>
                <option value="S2">S2</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Type</label>
              <select value={type} onChange={(e) => setType(e.target.value as any)}
                className="w-full px-2 py-2 text-sm bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-gray-100">
                <option value="normale">Normale</option>
                <option value="rattrapage">Rattrapage</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="block text-[10px] font-semibold text-gray-600 dark:text-gray-400 mb-1">Début dépôt</label>
              <input type="date" value={dateDebut} onChange={(e) => setDateDebut(e.target.value)}
                className="w-full px-2 py-1.5 text-xs bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-gray-100" />
            </div>
            <div>
              <label className="block text-[10px] font-semibold text-gray-600 dark:text-gray-400 mb-1">Fin dépôt</label>
              <input type="date" value={dateFin} onChange={(e) => setDateFin(e.target.value)}
                className="w-full px-2 py-1.5 text-xs bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-gray-100" />
            </div>
            <div>
              <label className="block text-[10px] font-semibold text-gray-600 dark:text-gray-400 mb-1">Examens</label>
              <input type="date" value={dateExamens} onChange={(e) => setDateExamens(e.target.value)}
                className="w-full px-2 py-1.5 text-xs bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-gray-100" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
              Nombre de sujets requis par matière
              <span className="ml-2 text-[10px] font-normal text-gray-500">
                ({type === 'rattrapage' ? 'défaut 1 pour rattrapage' : 'défaut 2 pour session normale'})
              </span>
            </label>
            <input
              type="number" min={1} max={10} value={nbSujetsRequis}
              onChange={(e) => setNbSujetsRequis(Math.max(1, parseInt(e.target.value) || 1))}
              className="w-24 px-3 py-2 text-sm bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-gray-100"
            />
            <p className="text-[10px] text-gray-500 mt-1">
              Chaque formateur devra déposer ce nombre de sujets par matière (sujet principal + sujet de secours pour session normale).
            </p>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Note (optionnel)</label>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={2000}
              className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-gray-100 resize-none" />
          </div>
          {/* Sélection des pôles (isolation) */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
              Pôles concernés
              <span className="ml-2 text-[10px] font-normal text-gray-500">
                {polesSelectionnes.length === 0
                  ? '(aucun coché = session globale, tous pôles)'
                  : `${polesSelectionnes.length} pôle${polesSelectionnes.length > 1 ? 's' : ''} sélectionné${polesSelectionnes.length > 1 ? 's' : ''}`}
              </span>
            </label>
            {polesDisponibles.length === 0 ? (
              <div className="text-[10px] text-gray-500 italic px-2 py-1 bg-gray-50 dark:bg-gray-900/40 rounded">
                {estAdmin ? 'Aucun pôle actif.' : 'Vous n\'avez accès à aucun pôle — contactez un administrateur.'}
              </div>
            ) : (
              <div className="flex flex-wrap gap-1.5 p-2 bg-gray-50 dark:bg-gray-900/40 rounded border border-gray-200 dark:border-gray-700">
                {polesDisponibles.map(nom => {
                  const coche = polesSelectionnes.includes(nom);
                  return (
                    <label key={nom}
                      className={`inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] cursor-pointer border ${
                        coche
                          ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-200 border-blue-300 dark:border-blue-700'
                          : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600'
                      }`}>
                      <input type="checkbox" className="h-3 w-3" checked={coche}
                        onChange={(e) => {
                          setPolesSelectionnes(prev => e.target.checked
                            ? Array.from(new Set([...prev, nom]))
                            : prev.filter(x => x !== nom));
                        }} />
                      <span>{nom}</span>
                    </label>
                  );
                })}
              </div>
            )}
            <p className="text-[10px] text-gray-500 mt-1">
              Les formateurs ne verront cette session que si l'un des pôles ci-dessus recoupe leurs matières.
              {estAdmin && ' Ne rien cocher rendra la session visible par tous les pôles.'}
            </p>
          </div>
          {erreur && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 rounded p-2 text-sm">
              {erreur}
            </div>
          )}
        </div>
        <footer className="px-5 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
          <button type="button" onClick={onClose}
            className="px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded">
            Annuler
          </button>
          <button type="submit" disabled={isLoading}
            className="px-4 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold rounded inline-flex items-center gap-1.5">
            {isLoading ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
            Créer
          </button>
        </footer>
      </form>
    </div>
  );
}

function ModalUploadManuel({ session, onClose, onDone }: {
  session: SessionDepot; onClose: () => void; onDone: () => void;
}) {
  const [formateurId, setFormateurId] = useState<number | null>(null);
  const [rechercheFormateur, setRechercheFormateur] = useState('');
  const [matiereId, setMatiereId] = useState<number | null>(null);
  const [option, setOption] = useState('');
  const [notes, setNotes] = useState('');
  const [fichierOrdinaire, setFichierOrdinaire] = useState<File | null>(null);
  const [fichierRattrapage, setFichierRattrapage] = useState<File | null>(null);
  const [erreur, setErreur] = useState('');
  const { data: enseignants } = useEnseignantsDisponiblesCompteQuery();
  const [uploader, { isLoading }] = useAdminUploaderDepotMutation();

  // Matières du formateur sélectionné (filtre dynamique)
  const anneeActive = typeof window !== 'undefined' ? localStorage.getItem('annee_academique') || '' : '';
  const { data: matieresFormateur = [] } = useGetFormateurMatieresQuery(
    { id: formateurId as number, annee_academique: anneeActive },
    { skip: !formateurId },
  );

  // Ne conserver que les matières dont le semestre LMD correspond au semestre
  // de la session (S1 → 1/3/5, S2 → 2/4/6) — évite de sélectionner une matière
  // d'un semestre passé.
  const semestresAutorises = session.semestre === 'S1' ? [1, 3, 5] : [2, 4, 6];
  const matieresDuFormateur = (matieresFormateur || []).filter(m =>
    semestresAutorises.includes(m.matiere_semestre),
  );

  const formateur = enseignants?.results.find(e => e.id === formateurId);
  const matiereSelect = matieresDuFormateur.find(m => m.matiere === matiereId);

  // Options disponibles pour la matière sélectionnée (une matière a
  // typiquement une seule option, mais on liste au cas où plusieurs
  // classes/options existent).
  const optionsPourMatiere = matiereSelect ? [matiereSelect.matiere_option || ''].filter(Boolean) : [];

  // Multi-options : détecte les autres matières de ce formateur avec le même
  // nom_matiere + niveau (tronc commun). Ex : Programmation C++ en L1RT ET
  // L1DI&GL → un seul upload pour les deux.
  const matieresPartagees = React.useMemo(() => {
    if (!matiereSelect) return [];
    const nomLower = matiereSelect.matiere_nom.trim().toLowerCase();
    return matieresDuFormateur.filter(m =>
      m.matiere_nom.trim().toLowerCase() === nomLower
      && m.matiere_niveau === matiereSelect.matiere_niveau
    );
  }, [matiereSelect, matieresDuFormateur]);

  const [optionsSelectionnees, setOptionsSelectionnees] = React.useState<Set<number>>(new Set());
  // Auto-sélectionner toutes les options partagées quand la matière change
  React.useEffect(() => {
    if (matieresPartagees.length > 0) {
      setOptionsSelectionnees(new Set(matieresPartagees.map(m => m.matiere)));
    } else {
      setOptionsSelectionnees(new Set());
    }
  }, [matieresPartagees.map(m => m.matiere).join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-sélectionner l'option quand la matière change
  React.useEffect(() => {
    if (matiereSelect && !option && matiereSelect.matiere_option) {
      setOption(matiereSelect.matiere_option);
    }
  }, [matiereSelect]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset matière quand formateur change
  React.useEffect(() => { setMatiereId(null); setOption(''); }, [formateurId]);

  // Filtrage local recherche formateur (typeahead)
  const listeFormateurs = (enseignants?.results || []).filter(e => {
    if (!rechercheFormateur.trim()) return true;
    const q = rechercheFormateur.toLowerCase();
    return e.nom_complet.toLowerCase().includes(q) || (e.specialite || '').toLowerCase().includes(q);
  });

  const validExt = (f: File) => {
    const ext = f.name.split('.').pop()?.toLowerCase() || '';
    return ['pdf', 'doc', 'docx'].includes(ext);
  };

  const handle = async (e: React.FormEvent) => {
    e.preventDefault();
    setErreur('');
    if (!formateur) { setErreur('Sélectionnez un formateur'); return; }
    if (!matiereSelect) { setErreur('Sélectionnez une matière'); return; }
    if (!fichierOrdinaire && !fichierRattrapage) {
      setErreur('Ajoutez au moins un fichier (ordinaire ou rattrapage)');
      return;
    }
    for (const f of [fichierOrdinaire, fichierRattrapage].filter(Boolean) as File[]) {
      if (!validExt(f)) {
        setErreur(`Format accepté : PDF, .docx, .doc (fichier « ${f.name} » invalide)`);
        return;
      }
    }
    const uploads: Array<{ file: File; type_sujet: 'ordinaire' | 'rattrapage' }> = [];
    if (fichierOrdinaire) uploads.push({ file: fichierOrdinaire, type_sujet: 'ordinaire' });
    if (fichierRattrapage) uploads.push({ file: fichierRattrapage, type_sujet: 'rattrapage' });

    // Multi-options : construire liste matiere_ids et mapping options
    const matiereIdsList = Array.from(optionsSelectionnees);
    const optionsMap: Record<string, string> = {};
    matieresPartagees.forEach(m => {
      if (optionsSelectionnees.has(m.matiere)) {
        optionsMap[String(m.matiere)] = m.matiere_option || '';
      }
    });

    try {
      for (const u of uploads) {
        const fd = new FormData();
        fd.append('session_id', String(session.id));
        fd.append('formateur_user_id', String(formateur.id));
        fd.append('formateur_nom', formateur.nom_complet);
        if (matiereIdsList.length > 1) {
          fd.append('matiere_ids', JSON.stringify(matiereIdsList));
          fd.append('options_par_matiere', JSON.stringify(optionsMap));
        } else {
          fd.append('matiere_id', String(matiereSelect.matiere));
        }
        fd.append('matiere_libelle', matiereSelect.matiere_nom);
        fd.append('matiere_code', matiereSelect.matiere_code || '');
        fd.append('niveau', matiereSelect.matiere_niveau);
        fd.append('option', option || matiereSelect.matiere_option || '');
        fd.append('notes_formateur', notes);
        fd.append('type_sujet', u.type_sujet);
        fd.append('fichier', u.file);
        await uploader(fd).unwrap();
      }
      onDone();
    } catch (err: any) {
      setErreur(err?.data?.detail || 'Erreur upload');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}>
      <form onSubmit={handle} onClick={(e) => e.stopPropagation()}
        className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md border border-gray-200 dark:border-gray-700 max-h-[92vh] overflow-y-auto">
        <header className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white inline-flex items-center gap-2">
              <Upload size={18} /> Upload manuel
            </h3>
            <div className="text-[11px] text-gray-500">Au nom d'un formateur (clé USB sur place)</div>
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={18} />
          </button>
        </header>

        <div className="p-5 space-y-3">
          {/* Formateur : recherche + sélection dans une liste filtrée */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
              Formateur (au nom de…) *
            </label>
            <input type="text" value={rechercheFormateur} onChange={(e) => setRechercheFormateur(e.target.value)}
              placeholder="Rechercher par nom ou spécialité…"
              className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-gray-100 mb-1" />
            <div className="max-h-40 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded">
              {listeFormateurs.length === 0 ? (
                <div className="px-3 py-2 text-xs text-gray-400 italic">Aucun formateur trouvé.</div>
              ) : listeFormateurs.slice(0, 40).map(e => (
                <button type="button" key={e.id}
                  onClick={() => setFormateurId(e.id)}
                  className={`w-full text-left px-3 py-1.5 text-xs border-b border-gray-100 dark:border-gray-700/50 last:border-b-0 transition ${
                    formateurId === e.id
                      ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-900 dark:text-blue-100 font-semibold'
                      : 'hover:bg-gray-50 dark:hover:bg-gray-700/40 text-gray-700 dark:text-gray-200'
                  }`}>
                  {e.nom_complet}
                  {e.specialite && <span className="text-[10px] text-gray-500 ml-2">· {e.specialite}</span>}
                  {!e.a_compte && <span className="text-[10px] text-amber-600 ml-2">· sans compte</span>}
                </button>
              ))}
            </div>
          </div>

          {/* Matière filtrée par formateur */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Matière *</label>
            <select value={matiereId || ''} onChange={(e) => setMatiereId(e.target.value ? Number(e.target.value) : null)}
              disabled={!formateur}
              className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-gray-100 disabled:opacity-60">
              <option value="">
                {!formateur
                  ? '— Choisir un formateur d\'abord —'
                  : `— ${matieresDuFormateur.length} matière(s) du ${session.semestre} —`}
              </option>
              {matieresDuFormateur.map(m => (
                <option key={m.id} value={m.matiere}>
                  {m.matiere_niveau} · {m.matiere_nom} ({m.matiere_option})
                </option>
              ))}
            </select>
            {formateur && matieresDuFormateur.length === 0 && (
              <div className="text-[10px] text-amber-600 dark:text-amber-400 mt-1">
                Aucune matière du semestre {session.semestre} attribuée à ce formateur pour l'année {anneeActive || 'active'}.
              </div>
            )}
            <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-1">
              Filtré sur le semestre <strong>{session.semestre}</strong> de la session.
            </div>
          </div>

          {/* Option (dérivée automatiquement de la matière) */}
          {matiereSelect && (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Niveau</label>
                <input value={matiereSelect.matiere_niveau} readOnly
                  className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded text-gray-500" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Option</label>
                <select value={option} onChange={(e) => setOption(e.target.value)}
                  className="w-full px-2 py-2 text-sm bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-gray-100">
                  {optionsPourMatiere.length > 0
                    ? optionsPourMatiere.map(o => <option key={o} value={o}>{o}</option>)
                    : <option value="">—</option>}
                </select>
              </div>
            </div>
          )}

          {/* Multi-options : matière partagée entre plusieurs options */}
          {matiereSelect && matieresPartagees.length > 1 && (
            <div className="rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/30 p-3">
              <div className="text-xs font-semibold text-blue-800 dark:text-blue-300 mb-1">
                Tronc commun : {matieresPartagees.length} options concernées
              </div>
              <p className="text-[10px] text-gray-600 dark:text-gray-400 mb-2">
                Ce formateur enseigne « {matiereSelect.matiere_nom} » dans plusieurs options.
                Cochez celles pour lesquelles ce sujet s'applique — un seul upload créera
                autant de dépôts que d'options cochées.
              </p>
              {/* Rendu compact en badges cliquables au lieu d'une liste
                  verticale : gain de place quand il y a beaucoup d'options
                  partagées (ex: L2BTP, L2GSA, L2GE, L2AU, ...). */}
              <div className="flex flex-wrap gap-1.5">
                {matieresPartagees.map(m => {
                  const active = optionsSelectionnees.has(m.matiere);
                  const codeClasse = m.matiere_classe || (m.matiere_option || '');
                  return (
                    <button
                      key={m.matiere}
                      type="button"
                      onClick={() => {
                        const next = new Set(optionsSelectionnees);
                        if (next.has(m.matiere)) next.delete(m.matiere); else next.add(m.matiere);
                        setOptionsSelectionnees(next);
                      }}
                      title={m.matiere_option || ''}
                      className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded border transition-colors ${
                        active
                          ? 'bg-blue-600 text-white border-blue-700 dark:border-blue-500'
                          : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:border-blue-400'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={active}
                        readOnly
                        className="rounded border-gray-300 dark:border-gray-600 pointer-events-none w-3 h-3"
                      />
                      <span className="font-mono">{codeClasse}</span>
                      {m.matiere === matiereId && (
                        <span className={`text-[9px] px-1 rounded ${active ? 'bg-blue-800/60 text-blue-100' : 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300'}`}>choisie</span>
                      )}
                    </button>
                  );
                })}
              </div>
              <div className="text-[10px] text-gray-500 mt-1 tabular-nums">
                {optionsSelectionnees.size} option(s) sélectionnée(s) →
                {' '}{optionsSelectionnees.size} dépôt(s) créés
              </div>
            </div>
          )}

          {/* Deux zones d'upload : ORD + RATT */}
          <div className="space-y-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/30 p-3">
            <div className="text-[11px] text-gray-500 mb-1">
              Sélectionnez le(s) sujet(s) à déposer (au moins un).
            </div>
            <div className="rounded border border-blue-200 dark:border-blue-900/50 bg-blue-50/50 dark:bg-blue-950/20 p-2">
              <div className="text-[11px] font-semibold text-blue-700 dark:text-blue-300 mb-1 flex items-center gap-1.5">
                <span className="inline-block w-2 h-2 rounded-full bg-blue-500" />
                Sujet — Session ordinaire (SO)
              </div>
              <input type="file" accept=".pdf,.doc,.docx"
                onChange={(e) => setFichierOrdinaire(e.target.files?.[0] || null)}
                className="w-full text-xs text-gray-700 dark:text-gray-300 file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:bg-blue-100 dark:file:bg-blue-900/40 file:text-blue-800 dark:file:text-blue-200" />
              {fichierOrdinaire && (
                <div className="text-[10px] text-gray-500 mt-1 truncate">
                  {fichierOrdinaire.name} ({(fichierOrdinaire.size / 1024 / 1024).toFixed(2)} Mo)
                </div>
              )}
            </div>
            <div className="rounded border border-amber-200 dark:border-amber-900/50 bg-amber-50/50 dark:bg-amber-950/20 p-2">
              <div className="text-[11px] font-semibold text-amber-700 dark:text-amber-300 mb-1 flex items-center gap-1.5">
                <span className="inline-block w-2 h-2 rounded-full bg-amber-500" />
                Sujet — Session de rattrapage (SR)
              </div>
              <input type="file" accept=".pdf,.doc,.docx"
                onChange={(e) => setFichierRattrapage(e.target.files?.[0] || null)}
                className="w-full text-xs text-gray-700 dark:text-gray-300 file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:bg-amber-100 dark:file:bg-amber-900/40 file:text-amber-800 dark:file:text-amber-200" />
              {fichierRattrapage && (
                <div className="text-[10px] text-gray-500 mt-1 truncate">
                  {fichierRattrapage.name} ({(fichierRattrapage.size / 1024 / 1024).toFixed(2)} Mo)
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Notes (optionnel)</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} maxLength={2000}
              className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-gray-100 resize-none" />
          </div>

          {erreur && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 rounded p-2 text-sm">
              {erreur}
            </div>
          )}
        </div>

        <footer className="px-5 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
          <button type="button" onClick={onClose}
            className="px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 rounded">Annuler</button>
          <button type="submit" disabled={isLoading}
            className="px-4 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded inline-flex items-center gap-1.5">
            {isLoading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
            Uploader
          </button>
        </footer>
      </form>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
// Sous-composant : bloc "Brassages associés" pour une session de dépôt
// ═══════════════════════════════════════════════════════════════════════════
interface BrassagesAssociesBlocProps {
  session: SessionDepot;
  brassagesUtilises: Array<{
    id: number; date: string; mode: string;
    nombre_etudiants: number; description: string;
  }>;
  brassagesDisponibles: Array<{
    id: number; date: string; mode: string;
    nombre_etudiants: number; nombre_sites_utilises: number;
    description: string;
  }>;
  brassageIdsPersistes: number[];
  onUpdated: () => void;
}

function BrassagesAssociesBloc({
  session, brassagesUtilises, brassagesDisponibles,
  brassageIdsPersistes, onUpdated,
}: BrassagesAssociesBlocProps) {
  const [updateSession, { isLoading }] = useUpdateSessionDepotMutation();
  const [showEdit, setShowEdit] = React.useState(false);
  const [selectedIds, setSelectedIds] = React.useState<Set<number>>(
    new Set(brassageIdsPersistes),
  );

  // Synchroniser quand la prop change (après refetch)
  React.useEffect(() => {
    setSelectedIds(new Set(brassageIdsPersistes));
  }, [brassageIdsPersistes.join(',')]);

  const modeAuto = brassageIdsPersistes.length === 0;

  const toggleId = (id: number) => {
    setSelectedIds(prev => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      return s;
    });
  };
  const selectionner4Recents = () => {
    setSelectedIds(new Set(brassagesDisponibles.slice(0, 4).map(b => b.id)));
  };
  const enregistrer = async () => {
    try {
      await updateSession({
        id: session.id,
        brassage_ids: Array.from(selectedIds),
      }).unwrap();
      setShowEdit(false);
      onUpdated();
    } catch (e: any) {
      alert(e?.data?.detail || 'Erreur enregistrement');
    }
  };

  return (
    <div className="mb-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-3">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="text-xs font-semibold text-blue-800 dark:text-blue-300 flex items-center gap-1.5">
            <FileText size={14} /> Brassages utilisés pour calculer les copies à imprimer
          </div>
          <div className="text-[11px] text-gray-600 dark:text-gray-400 mt-0.5">
            {modeAuto ? (
              <>Mode <strong>automatique</strong> — agrégation des brassages
                récents ({brassagesUtilises.length} brassage(s) actuellement utilisé(s)).</>
            ) : (
              <>Mode <strong>manuel</strong> — {brassageIdsPersistes.length} brassage(s) explicitement associé(s) à cette session.</>
            )}
          </div>
          {brassagesUtilises.length > 0 && (
            <ul className="mt-1 text-[11px] text-gray-600 dark:text-gray-400 space-y-0.5">
              {brassagesUtilises.map(b => (
                <li key={b.id}>
                  • Brassage #{b.id} — {new Date(b.date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                  {' '}· {b.mode.replace('par_', '')} · {b.nombre_etudiants} étudiants
                  {b.description ? ` · ${b.description.slice(0, 50)}` : ''}
                </li>
              ))}
            </ul>
          )}
        </div>
        <button
          onClick={() => setShowEdit(!showEdit)}
          className="text-xs px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded font-semibold shrink-0"
        >
          {showEdit ? 'Fermer' : 'Configurer les brassages'}
        </button>
      </div>

      {showEdit && (
        <div className="mt-3 pt-3 border-t border-blue-300 dark:border-blue-700">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <button onClick={selectionner4Recents}
              className="text-xs px-2 py-1 rounded bg-white dark:bg-gray-800 border border-blue-400 text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/40">
              ☑ Cocher les 4 plus récents
            </button>
            <button onClick={() => setSelectedIds(new Set(brassagesDisponibles.map(b => b.id)))}
              className="text-xs px-2 py-1 rounded bg-white dark:bg-gray-800 border border-blue-400 text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/40">
              ☑ Tout cocher ({brassagesDisponibles.length})
            </button>
            <button onClick={() => setSelectedIds(new Set())}
              className="text-xs px-2 py-1 rounded bg-white dark:bg-gray-800 border border-gray-400 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800">
              Aucun (mode auto)
            </button>
            <div className="flex-1" />
            <button onClick={enregistrer} disabled={isLoading}
              className="text-xs px-3 py-1.5 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white rounded font-semibold inline-flex items-center gap-1">
              {isLoading ? <Loader2 size={11} className="animate-spin" /> : <Check size={11} />}
              Enregistrer
            </button>
          </div>
          <div className="text-[11px] text-gray-500 dark:text-gray-400 mb-1.5">
            {selectedIds.size === 0
              ? '⚠ Aucun brassage sélectionné → retour au mode automatique (agrégation des brassages récents).'
              : `${selectedIds.size} brassage(s) sélectionné(s). Les effectifs seront calculés en agrégant ces brassages (chaque étudiant compté 1 fois dans le plus récent).`}
          </div>
          {brassagesDisponibles.length === 0 ? (
            <p className="text-xs text-amber-600 dark:text-amber-400">
              Aucun brassage disponible pour {session.annee_academique}. Générez un brassage dans le module « Brassage ».
            </p>
          ) : (
            <div className="max-h-64 overflow-y-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded p-2 space-y-1">
              {brassagesDisponibles.map(b => (
                <label key={b.id}
                  className="flex items-start gap-2 cursor-pointer p-1.5 hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded">
                  <input
                    type="checkbox"
                    checked={selectedIds.has(b.id)}
                    onChange={() => toggleId(b.id)}
                    className="mt-0.5"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium text-gray-800 dark:text-gray-200">
                      Brassage #{b.id}
                      <span className="text-gray-500 ml-1.5">
                        · {new Date(b.date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    <div className="text-[11px] text-gray-500 dark:text-gray-400">
                      Mode : {b.mode.replace('par_', '')} · {b.nombre_etudiants} étudiants · {b.nombre_sites_utilises} sites
                    </div>
                    {b.description && (
                      <div className="text-[11px] text-gray-600 dark:text-gray-300 mt-0.5 italic truncate">
                        {b.description}
                      </div>
                    )}
                  </div>
                </label>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
